import { useState } from 'react';
import { CircleCheck, Clock, History, Mail, UserCheck } from 'lucide-react';
import { getErrorMessage } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDateTime, hasPermission } from '@blimo/shared';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ConfirmDialog, EmptyState, toast } from '@blimo/ui';
import { SendDialog } from '../../components/send-dialog.jsx';
import { recipientsText, useInvalidateCheck } from './utils.js';

/**
 * Kassenprüfer der Liegenschaft (`meta.auditors`) mit Bestätigungsstand; „Bestätigung erfassen“ im Namen
 * eines Prüfers (z. B. schriftliche Bestätigung).
 */
export function AuditorsCard({ check, auditors, me }) {
  const api = useApi();
  const invalidate = useInvalidateCheck();
  const [target, setTarget] = useState(/** @type {any} */ (null));
  const canManage = hasPermission(me, 'document-checks.manage');
  const confirmedAt = (partyId) => check.confirmations?.find((c) => String(c.party?.id) === String(partyId))?.confirmed_at;

  async function confirm() {
    try {
      await api.admin.documentChecks.confirmOnBehalf(check.id, target.party_id);
      await invalidate();
      toast.success(`Bestätigung für ${target.name} erfasst`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Bestätigung konnte nicht erfasst werden.'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Kassenprüfer</CardTitle>
        <CardDescription>Aktuelle Kassenprüfer der Liegenschaft und ihr Bestätigungsstand.</CardDescription>
      </CardHeader>
      <CardContent>
        {auditors.length === 0 ? (
          <EmptyState compact icon={UserCheck} title="Keine Kassenprüfer" description="Der Liegenschaft ist derzeit kein Kassenprüfer zugeordnet." />
        ) : (
          <ul aria-label="Kassenprüfer" className="flex flex-col divide-y">
            {auditors.map((auditor) => {
              const at = confirmedAt(auditor.party_id);
              return (
                <li key={auditor.party_id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <span className="min-w-0 flex-1 font-medium">{auditor.name}</span>
                  {auditor.confirmed ? (
                    <Badge variant="success">
                      <CircleCheck aria-hidden="true" /> Bestätigt{at ? ` am ${formatDateTime(at)}` : ''}
                    </Badge>
                  ) : (
                    <Badge variant="warning">
                      <Clock aria-hidden="true" /> Offen
                    </Badge>
                  )}
                  {canManage && !auditor.confirmed && (
                    <Button size="sm" variant="outline" onClick={() => setTarget(auditor)}>
                      Bestätigung erfassen
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      <ConfirmDialog
        open={Boolean(target)}
        onOpenChange={(open) => !open && setTarget(null)}
        title="Bestätigung erfassen?"
        description={target ? `Die Prüfung wird im Namen von ${target.name} als bestätigt erfasst (z. B. nach schriftlicher Bestätigung).` : undefined}
        confirmLabel="Bestätigung erfassen"
        onConfirm={confirm}
      />
    </Card>
  );
}

const OWNER_MAILS = [
  {
    id: 'owner-info',
    label: 'Info an Eigentümer',
    description: 'Eigentümer informieren, dass Belege und Unterlagen im Portal bereitstehen.',
    sentKey: 'owner_info_sent_at',
    call: (api, id, options) => api.admin.documentChecks.ownerInfo(id, options),
  },
  {
    id: 'owner-documents',
    label: 'Unterlagen zur Eigentümerversammlung',
    description: 'Einladung mit den veröffentlichten Dokumenten aus „Vorbereitung zur Eigentümerversammlung“.',
    sentKey: 'documents_sent_at',
    call: (api, id, options) => api.admin.documentChecks.ownerDocuments(id, options),
  },
  {
    id: 'cost-statement-request',
    label: 'Anfrage Betriebskostenabrechnung',
    description: 'Angebot zur Erstellung der Betriebskostenabrechnung an die Eigentümer.',
    sentKey: 'cost_statement_request_sent_at',
    call: (api, id, options) => api.admin.documentChecks.costStatementRequest(id, options),
  },
];

/** Versand an die Eigentümer – jede Aktion mit Bestätigung; „bereits versendet“ entscheidet die API (409 → „Erneut senden“). */
export function OwnerMailsCard({ check, me }) {
  const api = useApi();
  const invalidate = useInvalidateCheck();
  const [active, setActive] = useState(/** @type {any} */ (null));
  const canSend = hasPermission(me, 'document-checks.release');

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Eigentümer informieren</CardTitle>
        <CardDescription>E-Mails an die aktuellen Eigentümer der Liegenschaft.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul aria-label="Versand an Eigentümer" className="flex flex-col divide-y">
          {OWNER_MAILS.map((mail) => {
            const sentAt = mail.sentKey ? check[mail.sentKey] : null;
            return (
              <li key={mail.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="font-medium">{mail.label}</span>
                  <span className="text-xs text-muted-foreground">{mail.description}</span>
                  {sentAt && <span className="text-xs text-muted-foreground">Zuletzt versendet am {formatDateTime(sentAt)}</span>}
                </span>
                {canSend && (
                  <Button size="sm" variant="outline" onClick={() => setActive(mail)}>
                    <Mail aria-hidden="true" /> Senden
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
      <SendDialog
        open={Boolean(active)}
        onOpenChange={(open) => !open && setActive(null)}
        title={active ? `${active.label} senden?` : ''}
        description={active?.description}
        send={(options) => active.call(api, check.id, options)}
        onSent={(result) => {
          invalidate();
          toast.success(recipientsText(result));
        }}
      />
    </Card>
  );
}

/** Ereignisprotokoll (neueste zuerst). @param {{ events?: Array<{ occurred_at: string, message: string }> }} props */
export function EventLog({ events }) {
  if (!events?.length) return <EmptyState icon={History} title="Keine Ereignisse" description="Es wurden noch keine Ereignisse protokolliert." />;
  const ordered = [...events].reverse();
  return (
    <ol aria-label="Ereignisprotokoll" className="flex flex-col divide-y rounded-md border">
      {ordered.map((event, index) => (
        <li key={`${event.occurred_at}-${index}`} className="flex flex-col gap-0.5 px-3 py-2 text-sm sm:flex-row sm:gap-4">
          <time dateTime={event.occurred_at} className="numeric shrink-0 text-muted-foreground sm:w-36">
            {formatDateTime(event.occurred_at)}
          </time>
          <span>{event.message}</span>
        </li>
      ))}
    </ol>
  );
}
